/**
 * Keeps the product info column pinned while the stacked gallery scrolls.
 *
 * When the column fits under the sticky header it is a plain sticky element
 * pinned just below the header. When it is taller than the viewport it
 * behaves like a "smart" sidebar: scrolling down lets the column scroll with
 * the page until its bottom edge reaches the viewport bottom, where it pins;
 * scrolling up releases it and pins by the top edge as soon as the title
 * comes back into view. Between the two pinned states it is positioned
 * relatively inside its wrapper so no jump is visible on direction change.
 */
(() => {
  const GAP = 24;
  const defaultQuery = window.matchMedia('(min-width: 750px)');

  const headerHeight = () => {
    const value = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
    return parseFloat(value) || 0;
  };

  class StickyColumn {
    constructor(column, query = defaultQuery) {
      this.column = column;
      this.query = query;
      this.wrapper = column.parentElement;
      this.mode = null;
      this.lastY = window.scrollY;
      this.frame = 0;
      this.onScroll = this.onScroll.bind(this);
      this.reset = this.reset.bind(this);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.reset);
      this.query.addEventListener('change', this.reset);
      new ResizeObserver(this.reset).observe(column);
      this.reset();
    }

    get topOffset() {
      return headerHeight() + GAP;
    }

    get fits() {
      return this.column.offsetHeight <= window.innerHeight - this.topOffset - GAP;
    }

    /** Distance from the column's normal-flow top to its current top. */
    get flowOffset() {
      const wrapperRect = this.wrapper.getBoundingClientRect();
      const paddingTop = parseFloat(getComputedStyle(this.wrapper).paddingTop) || 0;
      return this.column.getBoundingClientRect().top - (wrapperRect.top + paddingTop);
    }

    set(mode, top) {
      this.mode = mode;
      this.column.style.position = mode === 'relative' ? 'relative' : 'sticky';
      this.column.style.top = `${Math.round(top)}px`;
    }

    reset() {
      if (!this.query.matches) {
        this.mode = null;
        this.column.style.removeProperty('position');
        this.column.style.removeProperty('top');
        return;
      }
      if (this.fits) {
        this.set('top', this.topOffset);
        return;
      }
      // Tall column: start pinned by the top so the title is visible on load,
      // then hand over to the direction-aware logic on the first scroll.
      if (this.mode === 'bottom') {
        // Height changed while pinned by the bottom (an accordion opened):
        // release at the current spot so the next scroll re-pins to the new
        // bottom edge instead of leaving the extra content off screen.
        this.set('relative', Math.max(0, this.flowOffset));
      } else if (this.mode !== 'relative') {
        this.set('top', this.topOffset);
      }
      this.lastY = window.scrollY;
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    }

    update() {
      if (!this.query.matches || this.fits) return;

      const y = window.scrollY;
      const down = y > this.lastY;
      this.lastY = y;

      const rect = this.column.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const height = rect.height;
      const maxOffset = this.wrapper.clientHeight - (parseFloat(getComputedStyle(this.wrapper).paddingTop) || 0) - height;

      if (down) {
        if (this.mode === 'bottom') return;
        if (rect.bottom <= viewportHeight - GAP) {
          this.set('bottom', viewportHeight - height - GAP);
        } else if (this.mode === 'top') {
          // Release: freeze at the current spot and let it scroll with the page.
          this.set('relative', Math.min(Math.max(0, this.flowOffset), maxOffset));
        }
      } else {
        if (this.mode === 'top') return;
        if (rect.top >= this.topOffset) {
          this.set('top', this.topOffset);
        } else if (this.mode === 'bottom') {
          this.set('relative', Math.min(Math.max(0, this.flowOffset), maxOffset));
        }
      }
    }
  }

  const init = (root = document) => {
    root
      .querySelectorAll('.product__info-container.product__column-sticky:not([data-sticky-ready])')
      .forEach((column) => {
        column.dataset.stickyReady = 'true';
        new StickyColumn(column);
      });
    // Other templates opt in with data-sticky-column and their own breakpoint.
    root.querySelectorAll('[data-sticky-column]:not([data-sticky-ready])').forEach((column) => {
      column.dataset.stickyReady = 'true';
      const min = parseInt(column.dataset.stickyMin, 10) || 750;
      new StickyColumn(column, window.matchMedia(`(min-width: ${min}px)`));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  // Theme editor re-renders the section; pick up the fresh column.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
